import React, { useCallback } from 'react';
import styles from './conversion-factor-tile.scss';
import { ScreenField, ScreenPicker, ScreenPickerProps } from 'app/area/codetable/item/screen-renderer/types';
import { CodeItem, SubstanceConversionFactor } from 'app/area/codetable/item/types-store';
import { UoMFrom } from 'app/area/codetable/item/screen-renderer/screen-pickers/conversion-factors/uom-from';
import { UoMTo } from 'app/area/codetable/item/screen-renderer/screen-pickers/conversion-factors/uom-to';
import { Factor } from 'app/area/codetable/item/screen-renderer/screen-fields/conversion-factors/factor';
import { MeatballMenu, ValidationProps } from 'ui';
import { ReadOnlyType, UpdateConversionFactorPayload } from 'app/area/codetable/item/types';
import { createConversionFactorsId } from 'app/area/codetable/item/codetable-item-helpers';
import { CodeComplexValue } from 'app/area/shared/codetable/types';

export interface ConversionFactorTileProps extends ScreenPickerProps {
  conversionFactor: SubstanceConversionFactor;
  removeConversionFactor: (codeItem: CodeItem, toRemove: SubstanceConversionFactor,
    validation: { [key: string]: ValidationProps }) => void;
  updateConversionFactor: (payload: UpdateConversionFactorPayload) => void;
}

// tslint:disable-next-line:max-func-body-length
export const ConversionFactorTile: React.FC<ConversionFactorTileProps> = (props) => {
  const { clearByKey, updateValidation, updateByKey, resetValidation, loadCodetableOptions,
    breadcrumbs, codeItem, conversionFactor, translation,
    validation, codetableOptions, removeConversionFactor, updateConversionFactor, readOnlyConfig } = props;

  const updateUoMFrom = useCallback((value: CodeComplexValue<number>) => updateConversionFactor({
    value, key: ScreenPicker.uomFrom, codeItem, validation, conversionFactor
  }), [codeItem, validation, conversionFactor]);

  const updateUoMTo = useCallback((value: CodeComplexValue<number>) => updateConversionFactor({
    value, key: ScreenPicker.uomTo, codeItem, validation, conversionFactor
  }), [codeItem, validation, conversionFactor]);

  const updateFactor = useCallback((value: number) => updateConversionFactor({
    value, key: ScreenField.factor, codeItem, validation, conversionFactor
  }), [codeItem, validation, conversionFactor]);

  const remove = useCallback(() => removeConversionFactor(codeItem, conversionFactor, validation),
    [codeItem, conversionFactor, validation]);

  const readOnly = readOnlyConfig && readOnlyConfig[ReadOnlyType.all];

  const validationId = createConversionFactorsId(ScreenPicker.uomConversionType, conversionFactor?.uomConversionType?.value);

  const errors = validation?.[validationId]?.messages || [];
  const hasErrors = errors.length > 0;

  return <div className={styles.tileContainer}>
    <div className={`${styles.tile} ${hasErrors && styles.validationErrors}`}>
      <div className={styles.tileHeader}>
        <div>
          <h3>{conversionFactor?.uomConversionType?.description}</h3>
          {hasErrors && <div className={styles.validationMessageWrapper}>
            <ul>{errors.map((item, index) => <li key={index} className="list-group-item-danger">{item.message}</li>)}</ul>
          </div>}
          {!readOnly && <MeatballMenu className={styles.meatball} closeAfterClick={true}>
            <li>
              <button type="button" className="dropdown-item" onClick={remove}>
                {translation.remove}
              </button>
            </li>
          </MeatballMenu>}
        </div>
      </div>
      <ol className={styles.tileContent}>
        <div>
          <div>
            <div className="row">
              <UoMFrom
                breadcrumbs={breadcrumbs}
                categoryId={conversionFactor.uomTypeFrom.value}
                clearByKey={clearByKey}
                codetableOptions={codetableOptions}
                updateByKey={updateByKey}
                onChange={updateUoMFrom}
                codeItem={codeItem}
                resetValidation={resetValidation}
                updateValidation={updateValidation}
                loadCodetableOptions={loadCodetableOptions}
                translation={translation}
                value={conversionFactor.uomFrom}
                validation={validation}
                readOnlyConfig={readOnlyConfig}
                conversionFactor={conversionFactor} />
              <UoMTo
                breadcrumbs={breadcrumbs}
                categoryId={conversionFactor.uomTypeTo.value}
                clearByKey={clearByKey}
                codetableOptions={codetableOptions}
                updateByKey={updateByKey}
                onChange={updateUoMTo}
                codeItem={codeItem}
                resetValidation={resetValidation}
                updateValidation={updateValidation}
                loadCodetableOptions={loadCodetableOptions}
                translation={translation}
                value={conversionFactor.uomTo}
                validation={validation}
                readOnlyConfig={readOnlyConfig}
                conversionFactor={conversionFactor} />
            </div>
          </div>

          <div>
            <div className="row">
              <Factor
                codeItem={codeItem}
                onChange={updateFactor}
                resetValidation={resetValidation}
                translation={translation}
                updateByKey={updateByKey}
                value={conversionFactor.factor}
                validation={validation}
                readOnlyConfig={readOnlyConfig}
                conversionFactor={conversionFactor} />
            </div>
          </div>
        </div>
      </ol>
    </div>
  </div>;
};
